import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  Target, 
  Calculator, 
  Plane, 
  Brain,
  PieChart,
  Settings,
  Plus,
  ArrowRight,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { BudgetingService, BudgetSummary, SavingsGoalProgress } from '../../lib/budgetingService';
import { useAuth } from '../../contexts/AuthContext';

export function MobileBudgetingDashboard() {
  const { user } = useAuth();
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);
  const [savingsGoals, setSavingsGoals] = useState<SavingsGoalProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadBudgetData();
  }, []);

  const loadBudgetData = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const [summary, goals] = await Promise.all([
        BudgetingService.getBudgetSummary(user.id),
        BudgetingService.getSavingsGoalProgress(user.id)
      ]);

      setBudgetSummary(summary);
      setSavingsGoals(goals);
    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const getHealthColor = (savingsRate: number) => {
    if (savingsRate >= 20) return 'text-green-600';
    if (savingsRate >= 10) return 'text-blue-600';
    if (savingsRate >= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthIcon = (savingsRate: number) => {
    if (savingsRate >= 20) return <CheckCircle className="w-4 h-4" />;
    if (savingsRate >= 10) return <CheckCircle className="w-4 h-4" />;
    if (savingsRate >= 5) return <AlertCircle className="w-4 h-4" />;
    return <AlertCircle className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Budgeting</h1>
            <p className="text-gray-600">Loading your financial data...</p>
          </div>
          <div className="p-2 bg-blue-100 rounded-lg">
            <DollarSign className="w-6 h-6 text-blue-600" />
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budgeting</h1>
          <p className="text-gray-600">Manage your finances on the go</p>
        </div>
        <div className="p-2 bg-blue-100 rounded-lg">
          <DollarSign className="w-6 h-6 text-blue-600" />
        </div>
      </div>

      {/* Quick Stats */}
      {budgetSummary && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Net Amount</span>
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
            <p className={`text-xl font-bold ${budgetSummary.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(budgetSummary.netAmount)}
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Savings Rate</span>
              {getHealthIcon(budgetSummary.savingsRate)}
            </div>
            <p className={`text-xl font-bold ${getHealthColor(budgetSummary.savingsRate)}`}>
              {budgetSummary.savingsRate.toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <button className="flex items-center space-x-3 p-4 bg-white border rounded-lg hover:bg-gray-50">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calculator className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Budget</p>
              <p className="text-sm text-gray-500">Create & manage</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
          </button>

          <button className="flex items-center space-x-3 p-4 bg-white border rounded-lg hover:bg-gray-50">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Plane className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Estimates</p>
              <p className="text-sm text-gray-500">Track expenses</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
          </button>

          <button className="flex items-center space-x-3 p-4 bg-white border rounded-lg hover:bg-gray-50">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Target className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Goals</p>
              <p className="text-sm text-gray-500">Savings targets</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
          </button>

          <button className="flex items-center space-x-3 p-4 bg-white border rounded-lg hover:bg-gray-50">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Brain className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900">Decisions</p>
              <p className="text-sm text-gray-500">Get advice</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
          </button>
        </div>
      </div>

      {/* Savings Goals */}
      {savingsGoals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900">Savings Goals</h3>
          <div className="space-y-3">
            {savingsGoals.slice(0, 3).map((goal, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{goal.goal.name}</h4>
                  <span className="text-sm font-medium text-gray-700">
                    {goal.progress.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(goal.progress, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{formatCurrency(goal.goal.current_amount)} / {formatCurrency(goal.goal.target_amount)}</span>
                  {goal.daysRemaining > 0 && (
                    <span>{goal.daysRemaining} days left</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-center py-6">
            <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No recent transactions</p>
            <p className="text-sm text-gray-400">Add transactions to track your spending</p>
          </div>
        </div>
      </div>

      {/* Add Transaction Button */}
      <button className="w-full flex items-center justify-center space-x-2 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
        <Plus className="w-5 h-5" />
        <span>Add Transaction</span>
      </button>
    </div>
  );
}
